import { Component } from '@angular/core';
import { UntypedFormBuilder, Validators, UntypedFormGroup, FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { Communication } from 'src/providers/communication/communication';
import { Me } from 'src/shared/providers/me';
import { PrivateClient } from 'src/providers/private-client/private-client';
import { Timeout } from 'src/shared/components/timeout/timeout';
import { TimeoutErrorHandler } from 'src/shared/providers/http/timeout-error-handler';

import { InAppBrowserUtils } from 'src/shared/utils/in-app-browser-utils';

import { Loading } from 'src/shared/components/loading/loading';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { CommonUtils } from 'src/shared/utils/common-utils';

@Component({
    templateUrl: 'private-job-messages.html',
    standalone: false
})
export class PrivateJobMessagesPage extends Timeout {

  card: any;
  clientName: string;
  errorMessage: string;
  form: UntypedFormGroup;
  job: any;
  messages: any[] = [];
  submitted: boolean;
  timezone: any;
  pageNumber = 1;
  totalMessages = 0;
  isLoadingMore = false;
  readonly messagesPerPage = 50;

  constructor(
    public communication: Communication,
    private fb: UntypedFormBuilder,
    private iabUtils: InAppBrowserUtils,
    private me: Me,
    private navCtrl: CustomNavController,
    private privateClient: PrivateClient,
    public route: ActivatedRoute,
    router: Router,
    timeoutErrorHandler: TimeoutErrorHandler,
    private utils: CommonUtils
  ) {
    super(timeoutErrorHandler, router, route);
    this.form = this.fb.group({
      message: ['', Validators.required],
    });
  }

  @Loading()
  async ionViewDidEnter() {
    try {
      this.loaded = false;
      const jobId = this.route.snapshot.paramMap.get('jobId');
      this.job = this.navCtrl.getParam('job') || await this.privateClient.getJobDetailsForAddress(jobId, true);
      const result = await this.communication.getSharedInboxPaginated(this.job.customer.id, null, 1, this.messagesPerPage);
      this.messages = result.messages;
      this.totalMessages = result.totalRecords;
      this.pageNumber = 1;
      await this.markMessagesAsRead();
      this.clientName = this.job.customer.first_name + ' ' + this.job.customer.last_name;
      this.loaded = true;
    } catch (err) {
      this.timeoutHandler(err);
      this.errorMessage = err.error ? err.error.message : err.message;
      this.utils.showError(this.errorMessage);
    }
  }

  async markMessagesAsRead() {
    let array: any = [];
    this.messages.map((message) => {
      if (!message.is_read && message.from_member.type == 'CustomerMember' && message?.chat_room_key) {
        array.push((message?.chat_room_key));
      }
    });
    if (array.length > 0) {
      await this.communication.markMessagesAsRead(array);
    }
  }

  @Loading()
  async sendMessage(attachment = []) {
    this.errorMessage = '';
    this.submitted = true;
    if (attachment.length == 0 && !this.form.get('message').valid) {
      return;
    }
    const payload = {
      to_customer_id: this.job.customer.id,
      message: {
        text: this.form.value.message,
        files: attachment
      }
    };
    try {
      const response = await this.communication.sendSharedInboxMessage(payload);
      if (response?.data?.message == 'We couldn\'t send your message, please try again later!') {
        return this.errorMessage = response.data.message;
      }
      const msgResult = await this.communication.getSharedInboxPaginated(this.job.customer.id, null, 1, this.messagesPerPage);
      this.messages = msgResult.messages;
      this.totalMessages = msgResult.totalRecords;
      this.pageNumber = 1;
      // Clear the submitted flag before emptying the field so the composer
      // never renders an error/red state while we reset it after a successful send.
      this.submitted = false;
      this.form.patchValue({
        message: ''
      });
    } catch (err) {
      this.errorMessage = err.error ? err.error.message : err.message;
      this.utils.showError(this.errorMessage);
    }
  }

  async sendAttachment() {
    this.errorMessage = '';
    try {
      const attachment = await this.communication.addAttachment();
      if (attachment == '') {
        return this.errorMessage = 'Unable to attach photo. Please upload a PNG or JPEG file.';
      }
      await this.sendMessage([attachment]);
    } catch (err) {
      this.errorMessage =  err.error ? err.error.message : err.message;
      this.utils.showError(this.errorMessage);
    }
  }

  get hasMoreMessages(): boolean {
    return this.messages?.length < this.totalMessages;
  }

  async loadMoreMessages() {
    if (this.isLoadingMore || !this.hasMoreMessages) return;
    this.isLoadingMore = true;
    this.pageNumber++;
    try {
      const result = await this.communication.getSharedInboxPaginated(this.job.customer.id, null, this.pageNumber, this.messagesPerPage);
      this.messages = [...this.messages, ...result.messages];
    } catch {
      this.pageNumber--;
    } finally {
      this.isLoadingMore = false;
    }
  }

  trackByMessageId(index: number, message: any): any {
    return message.id || message.chat_room_key || index;
  }

  callAction() {
    this.iabUtils.openUrl(`tel://${this.job.customer.phone_number}`);
  }

}
